<script lang="ts" module>
	/** on page navigation, opens the dropdown to show this link being active */
	const showOnDropdown = (isActive: boolean | undefined) => {
		if (!isActive) return;

		return (element: HTMLAnchorElement) => {
			const parent = element.parentElement;
			if (!(parent instanceof HTMLDetailsElement)) return;

			parent.open = true;
		};
	};

	const feedFallbackIcon = (feedId: Feed["id"]): RawSvg => {
		if (feedId == "local") return Local;
		if (feedId == "bubble") return Bubble;
		return FeedIcon;
	};
</script>

<script lang="ts">
	import { type Snippet } from "svelte";
	import type { RawSvg } from "vite-plugin-magical-svg";

	import type { Cell } from "@-/data-loader/storage.svelte.ts";
	import { getCurrentRouteGroup, route } from "@-/router/lib.svelte.ts";
	import type { Feed } from "@-/social-api/feeds.ts";
	import { makeClientFor } from "@-/social-api/lib.ts";
	import { getSessions } from "@-/social-api/sessions.svelte.ts";
	import Icon from "#/lib/Icon.svelte";
	import { flow } from "#/lib/flow.module.css";
	import {
		Accounts,
		Appearance,
		Bluesky,
		Bookmarks,
		Bubble,
		ChevronRight,
		Dev,
		Error,
		Feed as FeedIcon,
		Feeds,
		Home,
		Local,
		Messages,
		Notifications,
		Search,
		Settings,
	} from "#/lib/icons.ts";
	import { toastErrors } from "./Toasts.svelte";

	const router = getCurrentRouteGroup();
	const sessions = getSessions();

	const allActive = $derived(
		toastErrors(
			await Promise.allSettled(
				Object.entries(sessions.active).map(([network, session]) =>
					makeClientFor(network, session),
				),
			),
		),
	);

	// done like this as to not block timeline loading with these
	let allFeeds = $state<(readonly [string, Cell<Feed[]>])[]>([]);
	const getAllFeeds = async () => {
		allFeeds = toastErrors(
			await Promise.allSettled(
				allActive.map(client =>
					client.feeds
						.new()
						.load(route.signal)
						.then(r => [client.network, r] as const),
				),
			),
		);
	};

	let notifCounts = $state<Cell<number>[]>([]);
	const notifCount = $derived(notifCounts.map(count => count.ref()).reduce((sum, c) => sum + c, 0));
	const getNotifCount = async () => {
		notifCounts = toastErrors(
			await Promise.allSettled(allActive.map(client => client.notifCount.new().load(route.signal))),
		);
	};

	$effect(() => {
		if (!sessions.isLoggedIn) return;

		void Promise.all([getAllFeeds(), getNotifCount()]);
	});
</script>

{#snippet flexIcon(icon: RawSvg | string | undefined, fallback?: RawSvg)}
	<!-- @wc-ignore -->
	{#if typeof icon == "string"}
		{#if fallback}
			{let failed = $state(false)}
			{#if failed}
				<Icon class={__styles.icon} icon={fallback} />
			{:else}
				<!-- svelte-ignore a11y_missing_attribute -->
				<img class={__styles.icon} src={icon} aria-hidden="true" onerror={() => (failed = true)} />
			{/if}
		{:else}
			<!-- svelte-ignore a11y_missing_attribute -->
			<img class={__styles.icon} src={icon} aria-hidden="true" />
		{/if}
	{:else if icon}
		<Icon class={__styles.icon} {icon} />
	{:else}
		<Icon class={__styles.icon} icon={fallback!} />
	{/if}
{/snippet}

{#snippet link(
	icon: RawSvg | string | undefined,
	label: string,
	href: string,
	opt?: { badge?: string; isNameUntrusted?: boolean; fallback?: RawSvg },
)}
	{const isActive = $derived(router.pattern?.test(href, location.origin))}

	<a {href} aria-current={isActive} class="_button" {@attach showOnDropdown(isActive)}>
		{#if opt?.badge}
			<div class="_badgeContainer">
				<div><div class="_badge">{opt.badge}</div></div>
				{@render flexIcon(icon, opt.fallback)}
			</div>
		{:else}
			{@render flexIcon(icon, opt?.fallback)}
		{/if}

		<span class={{ _untrustedName: opt?.isNameUntrusted }} data-label={label}>{label}</span>
	</a>
{/snippet}

{#snippet dropdown(icon: RawSvg | string, label: string, children: Snippet)}
	<details>
		<summary class="_button">
			{@render flexIcon(icon)}
			<span>{label}</span>
			<Icon class={__styles.dropdownIndicator} icon={ChevronRight} />
		</summary>

		{@render children()}
	</details>
{/snippet}

<!-- TODO: some way to collapse mobile sidebar -->
<nav>
	<div class="_floating {flow}">
		{#if sessions.isLoggedIn}
			{@render link(Home, "Home", "/")}
			{@render link(Notifications, "Notifications", "/notifications", {
				badge: notifCount > 0 ? notifCount.toString() : undefined,
			})}
			{@render link(Search, "Search", "/search")}
			{@render link(Messages, "Messages", "/messages")}
			{@render link(Bookmarks, "Bookmarks", "/bookmarks")}

			{@render dropdown(Feeds, "Feeds", feedsDropdown)}
			{#snippet feedsDropdown()}
				{#each allFeeds as [network, networkFeedsRef]}
					<!-- TODO: filter out local and bubble of the same network if multiple accounts from it are active -->
					{const networkFeeds = $derived(networkFeedsRef.ref())}
					{#each networkFeeds as feed}
						{@render link(feed.icon, feed.name, `/${network}/feed/${feed.id}`, {
							isNameUntrusted: true,
							fallback: feedFallbackIcon(feed.id),
						})}
					{/each}
				{/each}
			{/snippet}
		{/if}

		{@render dropdown(Settings, "Settings", settings)}
		{#snippet settings()}
			{@render link(Accounts, "Accounts", "/settings/accounts")}
			{@render link(Appearance, "Appearance", "/settings/appearance")}
			{@render link(Bluesky, "Bluesky", "/settings/bluesky")}
		{/snippet}

		{#if import.meta.env.DEV}
			<!-- @wc-ignore -->
			{@render dropdown(Dev, "Developer", developer)}
			{#snippet developer()}
				<!-- @wc-ignore -->
				{@render link(Dev, "Input Zoo", "/dev/zoo/input")}
				<!-- @wc-ignore -->
				{@render link(Dev, "Time Zoo", "/dev/zoo/time")}
				<!-- @wc-ignore -->
				{@render link(Dev, "Long Loading", "/dev/long-loading/1")}
				<!-- @wc-ignore -->
				{@render link(Dev, "Client Provider", "/dev/client/bluesky")}
				<!-- @wc-ignore -->
				{@render link(Error, "Error", "/dev/error")}
			{/snippet}
		{/if}
	</div>
</nav>

<style>
	:root {
		--c-sidebar-active: oklch(from var(--c-background) calc(l * 1.35) calc(c * 0.8) h);
	}

	nav {
		container-type: inline-size;
		grid-area: left;

		@container style(--viewport: mobile) {
			position: absolute;
			inset-block-start: 0;
			inline-size: 25%;
			margin-block-start: 0 !important; /* > :global(* + *) on App.svelte */
		}
	}

	._floating {
		--flow-size: var(--s-3xs);
		--_icon-size: 1.5rem;

		position: fixed;
		z-index: 1000;

		display: grid;
		justify-content: end;

		inline-size: 100cqi;

		@container style(--viewport: mobile) {
			display: block;

			block-size: 100%;
			padding: var(--s-xs);
			border-start-end-radius: var(--rad-3);
			border-end-end-radius: var(--rad-3);

			background: var(--g-light), var(--g-noise-darkBg), var(--c-panel-container-background);
			box-shadow: var(--shadow-panel-container);
		}
	}

	:local(.icon) {
		aspect-ratio: 1/1;
		inline-size: var(--_icon-size);
	}

	img:local(.icon) {
		translate: -2px 0; /* visual alignment */
		border-radius: var(--rad-1);
	}

	/* overlap the elements */
	._badgeContainer {
		display: grid;
		flex-shrink: 0;

		> :global(*) {
			grid-area: 1/1;
		}
	}

	._badge {
		position: absolute;
		translate: calc(var(--_icon-size) / 2) -25%;

		inline-size: fit-content;
		min-inline-size: calc((var(--s-3xs) * 2) + 1cap);
		padding: var(--s-3xs);
		border-radius: var(--rad-3);

		font-size: 0.8em;
		color: contrast-color(var(--c-opposite));
		text-align: center;

		background: var(--c-opposite);
		box-shadow: 0 0 calc(20px * var(--set-glowSpread)) 1px var(--c-opposite); /* a light glow */
	}

	._untrustedName {
		overflow-inline: clip;
		max-inline-size: 9ch; /* this may need to be adjusted as new items are added to the sidebar */
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	._button {
		--_hover-transition: 0.2s var(--ease-out);

		/* stylelint-disable-next-line defensive-css/no-user-select-none */
		user-select: none;

		position: relative;

		display: flex;
		gap: var(--s-3xs);
		align-items: center;

		padding: var(--s-2xs);

		font-weight: var(--w-bold);
		color: var(--c-text-bold);

		&::before {
			content: "";

			position: absolute;

			inline-size: 100%;
			block-size: 100%;
			margin-inline-start: calc(var(--_layout-gap) * -1);
			border: 1px solid oklch(from var(--c-panel-container-background) calc(l * 1.1) c h);
			border-radius: var(--rad-2);

			opacity: 0;
			background: var(--g-light), var(--g-noise), var(--c-sidebar-active);

			transition: var(--_hover-transition) opacity;
		}

		/* UX: names of elements that may get ellipsised will show in full on hover */
		._untrustedName {
			position: relative;

			&::before {
				--_padding: var(--s-2xs);

				content: attr(data-label);

				position: absolute;
				translate: 0 calc(var(--_padding) * -0.6);

				block-size: fit-content;
				padding: var(--_padding);
				padding-inline-start: 0;
				border-radius: var(--rad-2);

				opacity: 0;
				background: var(--c-sidebar-active);

				transition: var(--_hover-transition) opacity;
			}
		}

		span {
			white-space: nowrap;
		}

		> :global(*) {
			z-index: 1001;
		}
	}

	._button:is([aria-current="true"], :focus-visible, :hover) {
		outline: none;

		&::before {
			opacity: 1;

			@media (prefers-reduced-motion: no-preference) {
				view-transition-name: sidebar-active;
			}
		}

		._untrustedName {
			overflow: visible;

			&::before {
				opacity: 1;
			}
		}
	}

	details {
		--_transition: 0.2s var(--ease-out);

		&::details-content {
			translate: 0 -1lh;
			opacity: 0;
			transition:
				var(--_transition) opacity,
				var(--_transition) translate,
				var(--_transition) content-visibility allow-discrete;
		}

		summary {
			cursor: pointer;

			> :local(.dropdownIndicator) {
				margin-inline-start: auto;

				@media (prefers-reduced-motion: no-preference) {
					transition: var(--_transition) rotate;
				}
			}
		}

		&[open] {
			border-radius: var(--rad-2);
			outline: 1px solid oklch(from var(--c-panel-container-background) calc(l * 1.1) c h);

			@container style(--viewport: mobile) {
				outline-color: oklch(from var(--c-panel-container-background) calc(l * 1.3) c h);
			}

			&::details-content {
				translate: 0;
				opacity: 1;
			}

			> summary {
				/* stylelint-disable-next-line no-descending-specificity */
				&:is(:focus-visible, :hover)::before {
					border-block-start: 0;
					border-inline: 0;
					border-end-start-radius: 0;
					border-end-end-radius: 0;
				}

				> :local(.dropdownIndicator) {
					rotate: 90deg;
				}
			}

			._button:not(summary):is([aria-current="true"], :focus-visible, :hover) {
				&::before {
					border-inline: 0;
					border-start-start-radius: 0;
					border-start-end-radius: 0;
				}

				&:not(:last-child)::before {
					border-radius: 0;
				}

				&:last-child::before {
					border-block-end: 0;
				}
			}
		}
	}
</style>
